import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentProps,
  ElementType,
} from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { AuthenticationProvider } from 'graphql-ts-types';
import type {
  BackgroundProps,
  BorderProps,
  BoxShadowProps,
  DisplayProps,
  HeightProps,
  ResponsiveValue,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import { height } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { FillProps } from '../../theme/system-types';
import type { TextDecorationProps } from '../../theme/system-utilities';
import { textDecoration } from '../../theme/system-utilities';
import type { ThemedComponent } from '../../theme/theme';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import { Element } from '../../types/tracking';
import Spinner from '../Spinner';
import { ButtonBase, SPINNER_SIZE } from './Base';
import { ButtonBlack } from './Black';
import { ButtonGreen } from './Green';
import { ButtonPrimary } from './Primary';
import { ButtonSecondary, ButtonSecondary_V2 } from './Secondary';

export type ButtonKind = 'primary' | 'secondary' | 'secondary-v2' | 'bare' | 'black' | 'green';

export interface ButtonBaseProps {
  as?: ElementType;
  kind?: ButtonKind;
  scale?: 'small' | 'standard';
  isLoading?: boolean;
  spinnerSize?: ResponsiveValue<number>;
  spinnerStrokeColor?: string;
  spinnerMargin?: ResponsiveValue<string | number>;
  themeWidth?: ResponsiveValue<string | number>;
  themeHeight?: ResponsiveValue<string | number>;
  ariaLabel?: string;
  isRounded?: boolean;
}

interface ButtonTracking {
  eventName: string;
  text: string;
  placement?: string;
  element?: Element;
  provider?: AuthenticationProvider;
  interaction?: string;
  title?: string;
  voucher_name?: string;
}

export type ButtonTrackingProp = {
  tracking?: ButtonTracking;
};

export type ButtonWithHTMLAttrsProps<T = object> = FillProps &
  BoxShadowProps &
  ButtonBaseProps &
  ButtonTrackingProp &
  ButtonHTMLAttributes<unknown> &
  T;

export type StyledButtonBaseProps = DisplayProps &
  SpaceProps &
  BackgroundProps &
  BorderProps &
  WidthProps;

export type ThemedButton<T, P = unknown> = ThemedComponent<ButtonBaseProps & P, T>;

export type ThemedButtonWithHTMLAttrs<T = unknown> = ThemedButton<T, ButtonHTMLAttributes<unknown>>;

export type ThemedAnchorButtonWithHTMLAttrs<T = unknown> = ThemedComponent<
  ButtonBaseProps & AnchorHTMLAttributes<unknown>,
  StyledButtonBaseProps & T
>;

type AnchorButtonWithHTMLAttrs<T = object> = ButtonBaseProps &
  AnchorHTMLAttributes<HTMLAnchorElement> &
  StyledButtonBaseProps &
  T;

const trackButtonClick = (tracking?: ButtonTracking) => {
  if (tracking) {
    const {
      element = Element.Button,
      text,
      eventName,
      placement,
      provider,
      interaction,
      title,
      ...restTrackingParams
    } = tracking;
    dispatchTrackingEventGA4({
      event: eventName,
      element,
      [`${element}_text`]: text,
      ...(placement && { placement }),
      ...(provider && { provider }),
      ...(interaction && { interaction }),
      ...(title && { title }),
      ...restTrackingParams,
    });
  }
};

const getStyledButton = (kind: ButtonKind) => {
  switch (kind) {
    case 'primary':
      return ButtonPrimary;
    case 'secondary':
      return ButtonSecondary;
    case 'secondary-v2':
      return ButtonSecondary_V2;
    case 'black':
      return ButtonBlack;
    case 'green':
      return ButtonGreen;
    default:
      return ButtonBase;
  }
};

const getButtonHeight = (kind: ButtonKind, scale: ButtonBaseProps['scale']) => {
  if (kind === 'bare') {
    return 'auto';
  }

  switch (scale) {
    case 'small':
      return 32;
  }
};

const StyledSpinner = styled(Spinner)<HeightProps>`
  ${height}
`;

// eslint-disable-next-line complexity
const Button = (props: ButtonWithHTMLAttrsProps) => {
  const {
    type,
    kind = 'primary',
    scale = 'standard',
    isLoading,
    disabled,
    spinnerSize = SPINNER_SIZE,
    spinnerStrokeColor,
    spinnerMargin = 0,
    as: asTag = 'button',
    children,
    onClick,
    tracking,
    ariaLabel,
    isRounded,
    ...restProps
  } = props;

  const { t } = useLocalization();
  const StyledButton = getStyledButton(kind);

  const handleClick = (evt: React.MouseEvent) => {
    if (disabled) {
      evt.preventDefault();

      return;
    }

    trackButtonClick(tracking);
    onClick?.(evt);
  };

  return (
    <StyledButton
      {...restProps}
      kind={kind}
      type={type}
      {...(asTag ? { as: asTag } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(!type && asTag && asTag === 'button' ? { type: 'button' } : {})}
      {...(isLoading ? { 'aria-label': t('h24_loading'), isLoading: true } : {})}
      {...(disabled ? { 'aria-disabled': 'true', isDisabled: true } : { 'aria-disabled': false })}
      {...(isRounded ? { borderRadius: 'x-large' } : {})}
      minHeight={getButtonHeight(kind, scale)}
      onClick={handleClick}
    >
      {isLoading ? (
        <StyledSpinner
          height="100%"
          margin={spinnerMargin}
          size={spinnerSize}
          strokeColor={spinnerStrokeColor}
        />
      ) : (
        children
      )}
    </StyledButton>
  );
};

Button.displayName = 'Button';

const UnstyledButtonLink = styled(Button)<AnchorButtonWithHTMLAttrs<TextDecorationProps>>`
  ${textDecoration};
`;

const ButtonLink = (props: ComponentProps<typeof UnstyledButtonLink>) => (
  <UnstyledButtonLink textDecoration="none" as="a" {...props} />
);

export { ButtonBase, Button, ButtonLink };
